///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2002-2025, Open Design Alliance (the "Alliance").
// All rights reserved.
//
// This software and its documentation and related materials are owned by
// the Alliance. The software may only be incorporated into application
// programs owned by members of the Alliance, subject to a signed
// Membership Agreement and Supplemental Software License Agreement with the
// Alliance. The structure and organization of this software are the valuable
// trade secrets of the Alliance and its suppliers. The software is also
// protected by copyright law and international treaty provisions. Application
// programs incorporating this software must include the following statement
// with their copyright notices:
//
//   This application incorporates Open Design Alliance software pursuant to a
//   license agreement with Open Design Alliance.
//   Open Design Alliance Copyright (C) 2002-2025 by Open Design Alliance.
//   All rights reserved.
//
// By use of this software, its documentation or related materials, you
// acknowledge and accept the above terms.
///////////////////////////////////////////////////////////////////////////////

import { Vector3 } from "three";
import { type IDragger } from "@inweb/viewer-core";

import type { Viewer } from "../Viewer";
import { WalkControls } from "../controls/WalkControls";

export class WalkDragger implements IDragger {
  protected viewer: Viewer;
  public controls: WalkControls;

  constructor(viewer: Viewer) {
    this.controls = new WalkControls(viewer.camera, viewer.canvas);
    this.controls.addEventListener("change", this.controlsChange);
    this.controls.addEventListener("walkspeedchange", this.walkspeedChange);
    this.viewer = viewer;
    this.viewer.on("render", this.viewerRender);
    this.viewer.on("zoom", this.viewerZoom);
    this.updateControls();
  }

  dispose() {
    this.viewer.off("render", this.viewerRender);
    this.viewer.off("zoom", this.viewerZoom);
    this.controls.removeEventListener("walkspeedchange", this.walkspeedChange);
    this.controls.removeEventListener("change", this.controlsChange);
    this.controls.dispose();
  }

  updateControls = () => {
    const size = this.viewer.extents.getSize(new Vector3());
    this.controls.movementSpeed = Math.min(size.x, size.y, size.z) / 2;
  };

  controlsChange = () => {
    this.viewer.update();
  };

  walkspeedChange = (event: any) => {
    this.viewer.emitEvent(event);
  };

  viewerRender = () => {
    this.controls.update();
  };

  viewerZoom = () => {
    this.controls.rotateDelta.set(0, 0);
  };
}
